import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {
  useGetProductBySlugQuery,
  useGetRelatedProductsQuery,
  useGetProductVariationsQuery,
} from "../../api/productApi";
import {
  useProductVariationSelector,
  type UseProductVariationSelectorResult,
} from "../../hooks";
import ProductGallery from "../common/ProductGallery";
import ProductDetails from "./ProductDetails/ProductDetails";
import ProductPanels from "./ProductPanels";
import RelatedProducts from "./RelatedProducts";
import ProductBreadcrumbs from "./ProductBreadcrumbs";
import RTKQErrorCard from "../../../../../common/components/RTKQErrorCard";
import type {
  TransformedProduct,
  TransformedVariation,
} from "../../types/productApiTypes";
import type { RTKQueryError } from "../../../../../common/components/RTKQErrorCard";

const SingleProductContainer: React.FC = () => {
  const { t } = useTranslation("common");
  const { productSlug } = useParams<{ productSlug: string }>();

  const shopUrl = window.themeVariables?.shopUrl || "/shop/";

  const {
    data: product,
    isLoading: productIsLoading,
    isError: productIsError,
    error: productErrorData,
  } = useGetProductBySlugQuery(productSlug!, {
    skip: !productSlug,
  });

  const skipVariationsFetch =
    !product?.id ||
    product.type !== "variable" ||
    !product.enrichedVariations ||
    product.enrichedVariations.length === 0;

  const {
    data: variations = [] as TransformedVariation[],
    isLoading: variationsLoading,
    isError: variationsError,
    error: variationsErrorData,
  } = useGetProductVariationsQuery(product?.id!, {
    skip: skipVariationsFetch,
  });

  const {
    selectedAttributeSlugs,
    selectedVariation,
    handleAttributeSelect,
    variationSelectionComplete,
  }: UseProductVariationSelectorResult = useProductVariationSelector(
    product,
    variations,
    true
  );

  const isLoading = productIsLoading || !variationSelectionComplete;

  const {
    data: relatedProducts = [] as TransformedProduct[],
    isLoading: relatedProductsLoading,
  } = useGetRelatedProductsQuery(
    product?.relatedProductIds?.filter((id) => id !== product?.id) || [],
    {
      skip:
        !product?.id ||
        !product?.relatedProductIds ||
        product.relatedProductIds.length === 0,
    }
  );

  const isRelatedLoading = relatedProductsLoading;

  if (isLoading) {
    return (
      <div className="single-product-container">
        <div className="product-main">
          <div className="product-breadcrumbs">
            <Skeleton width={250} height={20} />
          </div>
          <div className="product-content">
            <div className="product-gallery-container">
              <Skeleton height={400} className="gallery-skeleton" />
              <div className="thumbnails-skeleton">
                <Skeleton
                  height={80}
                  width={80}
                  count={4}
                  inline={true}
                  style={{ marginRight: "10px" }}
                />
              </div>
            </div>
            <div className="product-details-container">
              <Skeleton height={36} width="80%" className="title-skeleton" />
              <Skeleton count={3} className="description-skeleton" />
              <div className="cart-form-skeleton">
                <Skeleton
                  height={30}
                  width="40%"
                  style={{ marginBottom: "8px" }}
                />
                <Skeleton height={42} style={{ marginBottom: "16px" }} />
                <Skeleton
                  height={30}
                  width="40%"
                  style={{ marginBottom: "8px" }}
                />
                <Skeleton height={42} style={{ marginBottom: "16px" }} />
              </div>
              <div className="meta-skeleton">
                <Skeleton count={3} width="60%" />
              </div>
            </div>
          </div>
          <div className="product-sections-container">
            <ProductPanels.Skeleton />
          </div>
          <div className="related-products-container">
            <RelatedProducts products={[]} isLoading={true} />
          </div>
        </div>
      </div>
    );
  }

  if (productIsError) {
    return (
      <div className="single-product-container single-product-container-error">
        <RTKQErrorCard
          error={productErrorData as RTKQueryError | null}
          title={t("productArchive.errorLoadingProduct")}
          maxHeight="300px"
        />
        <a
          href={shopUrl}
          className="button"
          style={{ marginTop: "1rem", display: "inline-block" }}
        >
          {t("productArchive.returnToShop")}
        </a>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="single-product-container single-product-container-not-found">
        <h1>{t("productArchive.productNotFound")}</h1>
        <p>{t("productArchive.productNotFoundMessage")}</p>
        <a href={shopUrl} className="button">
          {t("productArchive.returnToShop")}
        </a>
      </div>
    );
  }

  if (product.type === "variable" && variationsError) {
    console.error("Error fetching variations:", variationsErrorData);
  }

  return (
    <div className="single-product-container">
      <div className="product-main">
        <ProductBreadcrumbs product={product} />
        <div className="product-content">
          <div className="product-gallery-container">
            <ProductGallery
              product={product}
              selectedVariation={selectedVariation}
              variationsLoading={variationsLoading}
            />
          </div>
          <div className="product-details-container">
            <ProductDetails
              product={product}
              variationsLoading={variationsLoading}
              selectedVariation={selectedVariation}
              selectedAttributes={selectedAttributeSlugs}
              onAttributeSelect={handleAttributeSelect}
            />
          </div>
        </div>
        <div className="product-sections-container">
          <ProductPanels
            product={product}
            selectedVariation={selectedVariation}
            isLoading={isLoading}
          />
        </div>
        <div className="related-products-container">
          <RelatedProducts
            products={relatedProducts}
            isLoading={isRelatedLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default SingleProductContainer;
